import IcBagBackpack16 from "@traveloka/icon-kit-web/react/IcBagBackpack16";
import IcBagCarry16 from "@traveloka/icon-kit-web/react/IcBagCarry16";
import IcBagSuitcaseLargeCurve16 from "@traveloka/icon-kit-web/react/IcBagSuitcaseLargeCurve16";
import IcFacilitiesEntertainment16 from "@traveloka/icon-kit-web/react/IcFacilitiesEntertainment16";
import IcFacilitiesInternetWiFi16 from "@traveloka/icon-kit-web/react/IcFacilitiesInternetWiFi16";
import IcFacilitiesInternetWiFiUnavailable16 from "@traveloka/icon-kit-web/react/IcFacilitiesInternetWiFiUnavailable16";
import IcFacilitiesMeal16 from "@traveloka/icon-kit-web/react/IcFacilitiesMeal16";
import IcFacilitiesPowerPlug from "@traveloka/icon-kit-web/react/IcFacilitiesPowerPlug";
import IcFlightAirlines16 from "@traveloka/icon-kit-web/react/IcFlightAirlines16";
import IcTransportSeatClassMultiple16 from "@traveloka/icon-kit-web/react/IcTransportSeatClassMultiple16";
import IcTransportSeatLegRoom16 from "@traveloka/icon-kit-web/react/IcTransportSeatLegRoom16";

import { ContentGroup } from "../../../../core/resource/types";
import { formatMessage } from "../../../../utils";
import { Facility } from "../../FlightSearch/types";
import { FacilityItem, FacilityType } from "./types";

type FacilityExtended =
  | Facility
  | {
      type: Exclude<FacilityType, "baggage" | "meal">;
      available?: boolean;
      description?: string;
    };

export function getFacilityCatalog(
  facilities: FacilityExtended[],
  cr: ContentGroup
) {
  return facilities.reduce((acc, facility) => {
    switch (facility.type) {
      case "bag":
        acc[facility.type] = {
          type: "bag",
          getIcon: (size = 16) => (
            <IcBagBackpack16 width={size} height={size} />
          ),
          title: cr.facilityBagTitle,
          description: cr.facilityBagDescription,
        };
        break;
      case "cabin":
        acc[facility.type] = {
          type: "cabin",
          getIcon: (size = 16) => <IcBagCarry16 width={size} height={size} />,
          title: cr.facilityCabin,
        };
        break;
      case "baggage_paid":
        acc[facility.type] = {
          type: "baggage_paid",
          getIcon: (size = 16) => (
            <IcBagSuitcaseLargeCurve16 width={size} height={size} />
          ),
          title: formatMessage(cr.facilityBaggage, { spec: "0 kg" }),
        };
        break;
      case "baggage":
        acc[facility.type] = {
          type: "baggage",
          getIcon: (size = 16) => (
            <IcBagSuitcaseLargeCurve16 width={size} height={size} />
          ),
          title: formatMessage(cr.facilityBaggage, {
            spec:
              facility.quantity > 1
                ? `${facility.quantity} x ${facility.weight} kg`
                : `${facility.weight} kg`,
          }),
        };
        break;
      case "meal":
        acc[facility.type] = {
          type: "meal",
          getIcon: (size = 16) => (
            <IcFacilitiesMeal16 width={size} height={size} />
          ),
          title: cr.facilityMeal,
        };
        break;
      case "entertainment":
        acc[facility.type] = {
          type: "entertainment",
          getIcon: (size = 16) => (
            <IcFacilitiesEntertainment16 width={size} height={size} />
          ),
          title: cr.facilityEntertainment,
        };
        break;
      case "wifi":
        acc[facility.type] = {
          type: "wifi",
          getIcon: (size = 16) =>
            facility.available === false ? (
              <IcFacilitiesInternetWiFiUnavailable16
                width={size}
                height={size}
                color="#C0C4CC"
              />
            ) : (
              <IcFacilitiesInternetWiFi16 width={size} height={size} />
            ),
          title: cr.facilityWifi,
        };
        break;
      case "power":
        acc[facility.type] = {
          type: "power",
          getIcon: (size = 16) => (
            <IcFacilitiesPowerPlug width={size} height={size} />
          ),
          title: cr.facilityPower,
        };
        break;
      case "aircraft":
        acc[facility.type] = {
          type: "aircraft",
          getIcon: (size = 16) => (
            <IcFlightAirlines16 width={size} height={size} />
          ),
          title: facility.description || "",
        };
        break;
      case "seatLayout":
        acc[facility.type] = {
          type: "seatLayout",
          getIcon: (size = 16) => (
            <IcTransportSeatClassMultiple16 width={size} height={size} />
          ),
          title: facility.description || "",
        };
        break;
      case "seatPitch":
        acc[facility.type] = {
          type: "seatPitch",
          getIcon: (size = 16) => (
            <IcTransportSeatLegRoom16 width={size} height={size} />
          ),
          title: facility.description || "",
        };
        break;
      default:
        break;
    }

    return acc;
  }, {} as Record<FacilityType, FacilityItem>);
}

export function filterFacilities(
  facilities: FacilityItem[],
  types: readonly FacilityType[]
) {
  return facilities.filter((f) => types.includes(f.type));
}
